<template>
    <Dialog :visible="showDialog" class="action-popup forgot-password-done-popup" :modal="true" :draggable="false">
        <template #container>

            <div v-if="!emailSent">
                <div class="dialog-header">
                    Forgot Password?
                </div>
                <div class="dialog-content">
                    <span class="text text-center">Enter the email address you use to sign in.</span>
                    <span class="text text-center">An email with a link to reset your password will be sent.</span>
                    <span class="text text-center px-8 mx-4 mt-2">Make sure you enter the email address registered for your <strong>Softron License Manager</strong> account, not the one used on our Store or Help Desk.</span>
                    <div class="flex flex-column w-full justify-content-left mt-2">
                        <p class="subtitle">Email</p>
                        <div class="flex flex-column w-full relative" :class="{ 'mb-3': errorEmailMessage}">
                            <InputText ref="refInputTextUser" v-model="inputEm" :class="{ 'p-invalid': (errorEmailMessage && isValidEmail(inputEm)!='success') }"/>
                            <span v-if="errorEmailMessage" class="subtext absolute mb-2" style="top: 2.5rem; left: 0; color: var(--inputcomponent-invalid-color);">{{ errorEmailMessage }}</span>
                        </div>
                    </div>
                </div>
                <div class="dialog-footer">
                    <div class="buttons">
                        <ButtonModule type="secondary" severity="normal" label="Cancel" @click="close()"/>
                        <ButtonModule type="primary" severity="normal" :label="!httpForgotPasswordLoading? 'Send Email' : 'Wait ...'" @click="clickSubmit" :disabled="!inputEm || httpForgotPasswordLoading"/>
                    </div>
                </div>
            </div>

            <div v-else>
                <div class="dialog-header flex flex-row">
                    <div style="flex: 1;"></div>
                    <div style="flex: 5;" class="flex flex-row align-items-center justify-content-center">Password Reset Link Sent!</div>
                    <div style="flex: 1;" class="flex flex-row align-items-center justify-content-end">
                        <SvgCross class="cursor-pointer" className="close-cross-svg" color="var(--button-normal-color)" @click="$emit('close');"/>
                    </div>
                </div>
                <div class="dialog-content align-items-center text-center pt-4" style="padding-bottom: 1.2rem;">
                    <span class="text mb-3 sm:px-5 px-1">
                        If the email is registered and confirmed in our database, a link to reset your password has been sent to <span style="font-weight: var(--font-weight-normal);">{{ inputEm }}</span>.
                    </span>
                    <span class="text"> Please check your inbox.</span>
                </div>
                <div class="dialog-footer flex flex-column mt-0 pt-0 pb-4 text-center">
                    <!--<span class="text mb-4 flex flex-row gap-1 align-items-center">
                        Did not receive the email? Check your spams or wait a few minutes.
                        <div class="flex align-items-center" v-tooltip.top="{ value:`Note: Store and Help Desk accounts are separate and won't work here. To access the License Manager, please create a new account if you haven’t already.` }">
                            <SvgInfoCircle className="pluscircle-svg" color="var(--theme-font-color)"/>
                        </div>
                    </span> -->
                    <span class="text mb-4 flex flex-column gap-1 align-items-start">
                        Did not receive the email?
                        <span class="text flex flex-column gap-1 align-items-start justify-content-center">
                            <span>- Check your spam or junk folder.</span>
                            <span>- Confirm that you entered correctly the email associated with your <strong>Softron License Manager</strong> account.</span>
                            <span>- Emails from the <strong>Online Store</strong> or <strong>Help Desk</strong> are <strong>not linked</strong> and cannot be used here.</span>
                        </span>
                    </span>
                    <span class="text px-2 sm:px-8 text-center mb-1" style="font-style: italic; color: var(--inputcomponent-invalid-color);" v-if="emailSendingLimitText">{{ emailSendingLimitText }}</span>
                    <span class="text mb-1" style="font-style: italic; color: var(--mysuccess-color);" v-if="emailSentAgainText" >{{ emailSentAgainText }}</span>

                    <div v-if="!httpSendAgainLoading">
                        <span id="send-again-text" class="text cursor-pointer" @click="sendAgain">Send email again</span>
                    </div>
                    <div v-else>
                        <span class="text" style="opacity: 0.65;">Wait ...</span>
                    </div>
                </div>
            </div>

        </template>
    </Dialog>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, watchEffect, onMounted, onBeforeUnmount } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
// vue
import ButtonModule from '@/components/Modules/ButtonModule'
import SvgCross from '@/components/SVGs/SvgCross.vue';
import SvgInfoCircle from '@/components/SVGs/SvgInfoCircle.vue';
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';

const { isValidEmail } = InputsUtils()

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchForgotPassword, fetchSendAgain } = useApiRequest()
const httpForgotPasswordLoading = ref(false)
const httpForgotPasswordData = ref(null)
const httpSendAgainLoading = ref(false)
const httpSendAgainData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    currentEmail: String,
})
const { showDialog, currentEmail } = toRefs(props)

// --- VARIABLES ---
const inputEm = ref(null)
const emailSent = ref(true)

// Errors
const errorEmailMessage = ref(null)

// --- Validate add ---
const emailSentAgainText = ref(null)
const emailSendingLimitText = ref(null)
const clickSubmit = async () => {
    errorEmailMessage.value = null
    
    // Check if email is valid
    const messageValidity = isValidEmail(inputEm.value)
    if (messageValidity !== 'success') {
        errorEmailMessage.value = messageValidity
        return
    }
    // HTTP request
    httpForgotPasswordLoading.value = true
    try {
        httpForgotPasswordData.value = await fetchForgotPassword({ email: inputEm.value })
        emailSendingLimitText.value = false
        if (emailSent.value) { emailSentAgainText.value = "A new confirmation email has been sent." } // if emailSent = false, it means that it is the first time I try to send the email, so don't show that it has been sent again
    } catch (err) {
        handleForgotPasswordError(err)
    } finally {
        httpForgotPasswordLoading.value = false
        emailSent.value = true
        errorEmailMessage.value = null
    }
}
const sendAgain = async () => {
    errorEmailMessage.value = null
    
    // Check if email is valid
    const messageValidity = isValidEmail(inputEm.value)
    if (messageValidity !== 'success') {
        errorEmailMessage.value = messageValidity
        return
    }
    // HTTP request
    httpSendAgainLoading.value = true
    try {
        httpSendAgainData.value = await fetchSendAgain({ type: 'forgot_password', email: inputEm.value })
        emailSendingLimitText.value = false
        if (emailSent.value) { emailSentAgainText.value = "A new confirmation email has been sent." } // if emailSent = false, it means that it is the first time I try to send the email, so don't show that it has been sent again
    } catch (err) {
        handleForgotPasswordError(err)
    } finally {
        httpSendAgainLoading.value = false
        emailSent.value = true
        errorEmailMessage.value = null
    }
}
const handleForgotPasswordError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Forgot Password: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (error.response.status) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    toastService.add({ severity: 'warn', summary: "Forgot Password: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Forgot Password: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // Too much emails sent = EMAIL_SENDING_LIMIT_REACHED (401) => toast
                if (errorCode == 'EMAIL_SENDING_LIMIT_REACHED') {
                    emailSentAgainText.value = null
                    emailSendingLimitText.value = "The password reset email has been sent very recently. Please wait a few moment before trying to send the email again."
                }
                else { toastService.add({ severity: 'error', summary: `Forgot Password: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR / EMAIL_ERROR (500) => toast
                if (errorCode == 'EMAIL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Forgot Password: Email Sending.", detail: 'Our server did not manage sending the confirmation email to the address you provided. Make sure it is a valid address email.', life: 5000 });
                }
                else if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Forgot Password: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Forgot Password: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Forgot Password: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Forgot Password: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

/// --- Reset when closing ---
watchEffect(() => {
    if (!showDialog.value) {
        inputEm.value = (currentEmail.value && currentEmail.value.length>0) ? currentEmail.value : '';
        errorEmailMessage.value = null;
        emailSent.value = false
    }
})

// --- Focus + ENTER key listener ---
const refInputTextUser = ref(null)
watchEffect(() => {
    if (refInputTextUser.value) { refInputTextUser.value.$el.focus() }
})
onMounted(() => {
    window.addEventListener('keydown', handleKeyPressed)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyPressed)
}) 
const handleKeyPressed = (event) => {
    if (showDialog.value && event.keyCode === 13) { // ENTER
        if (inputEm.value && !emailSent.value) { clickSubmit() }
    }
}

// --- Close ---
const close = () => {
    emailSentAgainText.value = null
    emailSendingLimitText.value = null
    emit('close')
}
</script>

<style scoped>
/* Forgot password */
#send-again-text {
    text-decoration: none;
    color: var(--button-normal-color);
    border-bottom: 1px solid transparent;
}
#send-again-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>